import { useCallback, useEffect, useState } from "react";

import { Alert, type Flash } from "./components/Alert";
import { Boot } from "./components/Boot";
import { Shell } from "./components/Shell";
import { loadSession, logout } from "./lib/api";
import { CONSOLE_BASE } from "./lib/config";
import { messageOf } from "./lib/errors";
import { pushPath, redirect, replacePath } from "./lib/nav";
import { PERM, can } from "./lib/rbac";
import { currentRoute, type Route } from "./lib/routing";
import { loginURL, ssoParams } from "./lib/sso";
import type { Session } from "./lib/types";
import { GalleryScreen } from "./screens/GalleryScreen";
import { LoginScreen } from "./screens/LoginScreen";
import { MembersScreen } from "./screens/MembersScreen";
import { SitesScreen } from "./screens/SitesScreen";

/**
 * 포스트매스 통합 로그인 콘솔 — 루트.
 *
 * 두 가지 일을 합니다.
 *
 *  ① 로그인 진입(SSO) — 개별 서비스가 미로그인 사용자를 여기로 보냅니다:
 *       https://auth.<env>.postdemy.com/login?site=<site_code>&return_to=<서비스 복귀 URL>&state=<서비스 CSRF 값>
 *     이미 plt 세션이 살아 있으면 **화면을 보여주지 않고 즉시** API 의 `/auth/login` 으로 넘겨
 *     티켓을 받아 복귀시킵니다(Google 왕복은 서버가 건너뜁니다). 세션이 없으면 로그인 버튼을
 *     렌더하고, 버튼은 같은 파라미터를 그대로 API 로 전달합니다 — 콘솔은 site/return_to/state 를
 *     **해석하지 않고 전달만** 합니다. 검증(온보딩·복귀 허용·state 형식)은 서버의 책임입니다.
 *
 *  ② 콘솔 — 갤러리(서비스 이동) · 사용자 관리 · 서비스 카탈로그 관리.
 *
 * 보안 관련 불변식
 *  - 세션 쿠키는 `__Host-` host-only 이고 HttpOnly 라 JS 가 읽지 못합니다. 로그인 여부는 항상
 *    `GET /auth/session` 의 응답으로만 판정하며, 그 응답의 `csrfToken` 을 변경요청 헤더에 씁니다.
 *  - React 를 쓰므로 원본의 "innerHTML 금지" 규칙은 **구조적으로 보장**됩니다. 그래도 규칙은
 *    그대로입니다 — `dangerouslySetInnerHTML` 을 쓰지 마세요. 카탈로그의 이름·설명은 운영자
 *    입력이고 갤러리는 그 값을 모든 구성원에게 보여줍니다.
 *
 * 라우팅 라이브러리를 쓰지 않습니다 — 화면 5개에 history API 로 충분합니다(원본과 같은 선택).
 * 상태 관리 라이브러리도 없습니다: 서버가 모든 판정의 정본이고, 클라이언트 상태는 세션 한 덩이와
 * 화면별 목록뿐이라 `useState` 로 끝납니다.
 */

/**
 * 앱의 네 가지 국면.
 *
 * `anonymous` 가 `error` 를 들고 있는 것이 중요합니다 — **401(미로그인)과 API 장애를 구분**해야
 * 하고, 후자는 로그인 화면에 원인을 띄워야 합니다. 구분하지 않으면 API 장애가 "로그아웃된 것처럼"
 * 보여 사용자가 로그인을 반복하게 됩니다.
 */
type Phase =
  | { kind: "loading" }
  | { kind: "anonymous"; error: string | null }
  | { kind: "redirecting"; site: string }
  | { kind: "ready"; session: Session };

export function App() {
  const [route, setRoute] = useState<Route>(() => currentRoute());
  /**
   * revision 은 원본의 `boot()` 재호출을 대신합니다 — 변경요청이 끝나면 올려서 세션과 화면 데이터를
   * 함께 다시 읽습니다. 낙관적 갱신을 하지 않는 이유는 권한·상태 전이의 정본이 서버이고,
   * 화면이 서버와 어긋난 채로 남는 편이 한 번 더 읽는 비용보다 나쁘기 때문입니다.
   */
  const [revision, setRevision] = useState(0);
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [flash, setFlash] = useState<Flash | null>(null);

  const refresh = useCallback(() => {
    setRevision((r) => r + 1);
  }, []);

  /** 콘솔 내부 이동 — 히스토리에 쌓고 세션·데이터를 다시 읽습니다. */
  const navigate = useCallback((href: string) => {
    // 이동하면 이전 작업의 알림은 지웁니다(원본의 "한 번만 보여준다" 와 같은 결과).
    setFlash(null);
    pushPath(href);
    setRoute(currentRoute());
    setRevision((r) => r + 1);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setRoute(currentRoute());
      setRevision((r) => r + 1);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let alive = true;
    const params = ssoParams();

    loadSession()
      .then((session) => {
        if (!alive) return;

        // 미로그인 — 로그인 화면. 위임 파라미터는 버튼에 그대로 실립니다.
        if (!session) {
          setPhase({ kind: "anonymous", error: null });
          return;
        }

        // 로그인 상태 + 위임 진입 — 화면을 거치지 않고 바로 서버로 넘깁니다.
        // 서버가 OP 세션을 재사용해 Google 왕복 없이 티켓을 발급하고 서비스로 복귀시킵니다.
        if (params.isDelegated) {
          setPhase({ kind: "redirecting", site: params.site });
          redirect(loginURL(params));
          return;
        }

        if (route === "/login") {
          // 로그인 상태에서 `/login` 에 머무르면 갤러리로 정규화합니다(뒤로가기 루프 방지).
          replacePath(`${CONSOLE_BASE}/gallery`);
          setRoute("/gallery");
        } else if (
          // 권한이 없는 관리 화면에 직접 진입한 경우에도 갤러리로 보냅니다 — 서버가 403 을 내기
          // 전에 사용자가 "메뉴에 없는 화면" 을 보지 않게 합니다(권한 판정의 정본은 서버입니다).
          (route === "/members" && !can(session, PERM.memberManage)) ||
          (route === "/sites" && !can(session, PERM.siteManage))
        ) {
          setFlash({ kind: "warn", text: "이 화면에 접근할 권한이 없습니다." });
          replacePath(`${CONSOLE_BASE}/gallery`);
          setRoute("/gallery");
        }

        setPhase({ kind: "ready", session });
      })
      .catch((err: unknown) => {
        // 네트워크/서버 오류 — 세션을 버리고 원인을 로그인 화면에 띄웁니다.
        if (alive) setPhase({ kind: "anonymous", error: messageOf(err) });
      });

    return () => {
      alive = false;
    };
  }, [revision, route]);

  const onLogout = useCallback(async () => {
    if (phase.kind !== "ready") return;
    let error: string | null = null;
    try {
      await logout(phase.session.csrfToken);
    } catch (err: unknown) {
      // 실패해도 로컬 세션 상태는 버립니다(쿠키가 남아 있으면 다음 `/auth/session` 이 정정합니다).
      // 원인은 로그인 화면의 오류 자리에 띄웁니다 — 원본은 flash 로 넣었지만 로그인 화면이
      // flash 를 렌더하지 않아 실제로는 사라졌습니다. 이 경로만 의도적으로 고쳤습니다.
      error = `로그아웃 실패: ${messageOf(err)}`;
    }
    setFlash(null);
    setPhase({ kind: "anonymous", error });
    replacePath(`${CONSOLE_BASE}/login`);
    setRoute("/login");
  }, [phase]);

  if (phase.kind === "loading") return <Boot text="로그인 상태 확인 중" />;

  if (phase.kind === "anonymous") {
    return <LoginScreen params={ssoParams()} error={phase.error} />;
  }

  if (phase.kind === "redirecting") {
    return <Boot text={`${phase.site} 로 이동 중`} />;
  }

  const { session } = phase;

  return (
    <Shell route={route} session={session} onNavigate={navigate} onLogout={onLogout}>
      {flash ? <Alert kind={flash.kind}>{flash.text}</Alert> : null}

      {route === "/members" ? (
        <MembersScreen
          session={session}
          revision={revision}
          onFlash={setFlash}
          onRefresh={refresh}
        />
      ) : route === "/sites" ? (
        <SitesScreen session={session} revision={revision} onFlash={setFlash} onRefresh={refresh} />
      ) : (
        <GalleryScreen session={session} revision={revision} />
      )}
    </Shell>
  );
}
